import { supabase } from './supabaseClient';
import { buildCurriculumFromAssignments } from '../utils/parentDashboardData';

const CHILD_SELECT = 'id, name, grade, avatar_url, household_id, created_at, auth_user_id, archived_at';

export async function fetchCurrentUserPrimaryChild() {
  const {
    data: { user },
    error: userError
  } = await supabase.auth.getUser();

  if (userError) throw userError;
  if (!user) throw new Error('You must be signed in.');

  // Student session: the linked child row for this auth user.
  const { data: ownChild, error: ownChildError } = await supabase
    .from('children')
    .select(CHILD_SELECT)
    .eq('auth_user_id', user.id)
    .maybeSingle();

  if (ownChildError) throw ownChildError;
  if (ownChild) return ownChild;

  const { data: household, error: householdError } = await supabase
    .from('households')
    .select('id')
    .eq('owner_id', user.id)
    .maybeSingle();

  if (householdError) throw householdError;
  if (!household) throw new Error('No household found for this account.');

  const { data: children, error: childrenError } = await supabase
    .from('children')
    .select(CHILD_SELECT)
    .eq('household_id', household.id)
    .is('archived_at', null)
    .order('created_at', { ascending: true })
    .limit(1);

  if (childrenError) throw childrenError;

  const child = children?.[0];
  if (!child) throw new Error('No child found in this household.');

  return child;
}

/**
 * Returns all children in the current parent's household, ordered by created_at.
 * Unlike fetchCurrentUserPrimaryChild(), this does not take the student
 * (auth_user_id) shortcut — it always resolves via the owned household.
 */
export async function fetchAllChildrenForCurrentUser() {
  const {
    data: { user },
    error: userError
  } = await supabase.auth.getUser();

  if (userError) throw userError;
  if (!user) throw new Error('You must be signed in.');

  const { data: household, error: householdError } = await supabase
    .from('households')
    .select('id')
    .eq('owner_id', user.id)
    .maybeSingle();

  if (householdError) throw householdError;
  if (!household) throw new Error('No household found for this account.');

  const { data: children, error: childrenError } = await supabase
    .from('children')
    .select(CHILD_SELECT)
    .eq('household_id', household.id)
    .is('archived_at', null)
    .order('created_at', { ascending: true });

  if (childrenError) throw childrenError;

  return children || [];
}

export async function fetchCurriculumAndSessionsForChild(childId) {
  const [assignmentsResult, sessionsResult] = await Promise.all([
    supabase
      .from('curriculum_assignments')
      .select(`
        id,
        status,
        assigned,
        progress_percent,
        lesson_id,
        lessons (
          id,
          title,
          description,
          sequence_order,
          subjects (
            id,
            name
          )
        )
      `)
      .eq('child_id', childId),
    supabase
      .from('sessions')
      .select('id, lesson_id, started_at, duration_seconds, created_at')
      .eq('child_id', childId)
      .order('started_at', { ascending: false })
  ]);

  if (assignmentsResult.error) throw assignmentsResult.error;
  if (sessionsResult.error) throw sessionsResult.error;

  const sessions = sessionsResult.data || [];
  const curriculum = buildCurriculumFromAssignments(
    assignmentsResult.data || [],
    sessions
  );

  return { curriculum, sessions };
}

export async function fetchPrimaryChildCurriculumData() {
  const child = await fetchCurrentUserPrimaryChild();
  const { curriculum, sessions } = await fetchCurriculumAndSessionsForChild(child.id);
  return { child, curriculum, sessions };
}
